import { clsx } from "clsx";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren
} from "react";

type ReportsWrapperProps = PropsWithChildren<{
  className?: string;
  onScrollRef?: (el: HTMLDivElement | null) => void;
}>;

export const ReportsWrapper = ({
  children,
  className,
  onScrollRef
}: ReportsWrapperProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onScrollRef?.(ref.current);
  }, [onScrollRef]);

  return (
    <div
      ref={ref}
      className={clsx(
        "h-full rounded-md bg-background-secondary p-5",
        className
      )}
    >
      {children}
    </div>
  );
};

type WrapperGradientProps = {
  scrollRef?: HTMLDivElement | null;
};

export const WrapperGradient = ({ scrollRef }: WrapperGradientProps) => {
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(true);

  const updateScroll = useCallback(() => {
    if (!scrollRef) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef;
    setCanScrollUp(scrollTop > 0);
    setCanScrollDown(scrollTop + clientHeight < scrollHeight - 1);
  }, [scrollRef]);

  useEffect(() => {
    if (!scrollRef) return;

    updateScroll();
    scrollRef.addEventListener("scroll", updateScroll, { passive: true });

    const observer = new ResizeObserver(updateScroll);
    observer.observe(scrollRef);

    return () => {
      scrollRef.removeEventListener("scroll", updateScroll);
      observer.disconnect();
    };
  }, [scrollRef, updateScroll]);

  return (
    <>
      <div
        className={clsx(
          "pointer-events-none absolute right-0 top-0 w-full transition-opacity duration-200",
          canScrollUp ? "opacity-100" : "opacity-0"
        )}
      >
        <div
          className="h-[60px] w-full rounded-t-md"
          style={{
            background:
              "linear-gradient(0deg, rgba(242, 245, 249, 0.00) 0%, #F2F5F9 100%)"
          }}
        />
      </div>
      <div
        className={clsx(
          "pointer-events-none absolute bottom-0 right-0 w-full transition-opacity duration-200",
          canScrollDown ? "opacity-100" : "opacity-0"
        )}
      >
        <div
          className="h-[60px] w-full rounded-b-md"
          style={{
            background:
              "linear-gradient(180deg, rgba(242, 245, 249, 0.00) 0%, #F2F5F9 100%)"
          }}
        />
      </div>
    </>
  );
};
